import { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Button } from './ui/button';
import { useScrollToSection } from '../hooks/useScrollToSection';
import { useActiveSection } from '../hooks/useActiveSection';

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { scrollToSection } = useScrollToSection();

  const sectionIds = ['hero', 'stages', 'about', 'features', 'services', 'location', 'contact'];
  const activeSection = useActiveSection(sectionIds);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (id: string) => {
    scrollToSection(id);
    setMobileMenuOpen(false);
  };

  const navItems = [
    { label: 'Inicio', id: 'hero' },
    { label: 'Etapas', id: 'stages' },
    { label: 'Sobre Nosotros', id: 'about' },
    { label: 'Características', id: 'features' },
    { label: 'Servicios', id: 'services' },
    { label: 'Ubicación', id: 'location' },
    { label: 'Contacto', id: 'contact' },
  ];

  const textTone = scrolled ? 'text-slate-700' : 'text-white';
  const accentTone = scrolled ? 'text-slate-900' : 'text-white';

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 border-b border-slate-200 shadow-sm backdrop-blur-xl'
          : 'bg-slate-950/25 border-b border-white/10 backdrop-blur-md'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          <motion.button
            type="button"
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            onClick={() => scrollToSection('hero')}
            className="flex items-center text-left"
          >
            <div className="flex flex-col leading-none">
              <span className={`text-xl md:text-2xl font-extrabold tracking-tight ${accentTone}`}>
                Free Zone
              </span>
              <span className={`text-sm md:text-base font-semibold tracking-[0.18em] uppercase ${scrolled ? 'text-slate-500' : 'text-slate-200'}`}>
                Logistic Park
              </span>
            </div>
          </motion.button>

          <nav className="hidden md:flex items-center gap-6" role="navigation" aria-label="Navegación principal">
            {navItems.map((item, index) => {
              const isActive = activeSection === item.id;
              return (
                <motion.button
                  key={item.id}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * index }}
                  onClick={() => handleNavClick(item.id)}
                  className={`text-sm font-medium tracking-wide transition-colors relative group ${
                    isActive
                      ? scrolled
                        ? 'text-slate-900 font-semibold'
                        : 'text-white font-semibold'
                      : `${textTone} hover:text-slate-900`
                  }`}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {item.label}
                  <span
                    className={`absolute -bottom-1 left-0 h-px transition-all ${
                      isActive ? 'w-full' : 'w-0 group-hover:w-full'
                    } ${scrolled ? 'bg-slate-800' : 'bg-white'}`}
                  />
                </motion.button>
              );
            })}

            <Button
              onClick={() => handleNavClick('contact')}
              className="bg-slate-900 hover:bg-slate-800 text-white shadow-sm"
            >
              Contáctanos
            </Button>
          </nav>

          <button
            type="button"
            className={`md:hidden p-2 rounded-lg ${scrolled ? 'text-slate-700 hover:bg-slate-100' : 'text-white hover:bg-white/10'}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-slate-200 overflow-hidden"
          >
            <nav className="px-4 py-4 space-y-2">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => handleNavClick(item.id)}
                  className="block w-full text-left px-4 py-3 rounded-lg text-slate-700 hover:bg-slate-50 hover:text-slate-950 transition-colors"
                >
                  {item.label}
                </button>
              ))}
              <Button onClick={() => handleNavClick('contact')} className="w-full bg-slate-900 hover:bg-slate-800 text-white">
                Contáctanos
              </Button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
